import {
  ChangeDetectionStrategy, ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from "@angular/core";
import * as _ from "lodash";
import {
  CdkDrag,
  CdkDragDrop,
  CdkDragEnter,
  CdkDropList
} from "@angular/cdk/drag-drop";
import {RoutePanelModel} from "../../../models/route-panel.model";
import {RoutesViewSettingsModel} from "../../../models/routes-view-settings.model";
import {TaskModel} from "../../../../tasks/models/task.model";
import {TaskType} from "../../../../tasks/types/task.type";
import {RouteModel} from "../../../models/route.model";
import {RoutesComponentStore} from "../../state/routes.store";
import {Observable, Subscription} from "rxjs";
import * as moment from "moment";
import {RoutesQueryModel} from "../../../models/routes-query.model";
import {
  ResourceAllocationTimeWindowExtModel
} from "../../../../resources/models/resource-allocation-time-window-ext.model";
import {sortBy} from "lodash";
import {ResourcesComponentStore} from "../../../../resources/angular/state/resources.store";
import {RouteStatusEnum} from "../../../enums/route-status.enum";
import {RoutesFacadeService} from "../../../../lean-planning/angular/services/routes-facade.service";
import {RoutesDragAndDropService} from "../../services/routes-drag-and-drop.service";

require('./routes-panel-resource-view.scss');
@Component({
  standalone: false,
  selector: 'app-routes-panel-resource-view',
  templateUrl: './routes-panel-resource-view.html',
  providers: [RoutesComponentStore, ResourcesComponentStore],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RoutesPanelResourceViewComponent implements OnInit, OnChanges {

  @Input() index: any;
  @Input() panel: RoutePanelModel;
  @Input() resource: ResourceAllocationTimeWindowExtModel;
  @Input() panels: RoutePanelModel[];
  @Input() resourcesDropListIds: string[];
  @Input() settings: RoutesViewSettingsModel;
  @Input() selectedServiceIds: string[];
  @Input() visibleIds: string[];
  @Output() onChangeResourceSequence = new EventEmitter<{ routeGroupId: string, orderedRouteIds: string[] }>();
  @Output() onUpdateRouteResource = new EventEmitter<{routeId: string, resource: ResourceAllocationTimeWindowExtModel}>();
  @Output() onAddServicesToResource = new EventEmitter<{resource: ResourceAllocationTimeWindowExtModel, serviceIds: string[]}>();
  @Output() onClose = new EventEmitter<any>();
  @Output() onDrillDownRoute = new EventEmitter<{ routeId: string, event: any }>();
  @Output() onToggleVisibleRoute = new EventEmitter<{ route: RouteModel, isVisible: boolean }>();
  @Output() onChangeRouteInfo = new EventEmitter<{route: any, event: any}>();

  public routes: RouteModel[];
  public allPanelIds: string[];
  public selectedKeys: Object;
  public visibleKeys: { [key: string]: boolean } = {};
  public isDragging: boolean = false;
  protected readonly routeStatusType = RouteStatusEnum;

  loading$: Observable<boolean>;
  private routeSubscription: Subscription;
  private resourceSubscription: Subscription;
  private pendingRoutes: RouteModel[];
  private routeMetaDataUpdatedSubscription: Subscription;

  constructor(
    @Inject(RoutesComponentStore) private routesStore: RoutesComponentStore,
    @Inject(ResourcesComponentStore) private resourcesStore: ResourcesComponentStore,
    @Inject(ChangeDetectorRef) private cdr: ChangeDetectorRef,
    @Inject('$timeout') private $timeout,
    @Inject(RoutesFacadeService) private routesFacadeService: RoutesFacadeService,
    @Inject(RoutesDragAndDropService) private routesDragAndDropService: RoutesDragAndDropService,
    @Inject('leanPlanningService') private leanPlanningService: any) {
  }

  onToggleLockedRoute(event: {id: string, locked: boolean}) {
    this.leanPlanningService.editBlockedState(event.id, event.locked);
  }

  ngOnInit() {
    this.sortPredicate = this.sortPredicate.bind(this)

    this.loading$ = this.routesStore.loading$;

    this.routeMetaDataUpdatedSubscription = this.routesFacadeService.routeMetaDataUpdated$.subscribe((payload) => {
      if (payload?.id && payload?.metaData) {
        this.routesStore.updateRoute({
          id: payload.id,
          metaData: payload.metaData,
          updatedTime: payload.updatedTime,
          updatedAt: payload.updatedAt
        });
      }
    });

    this.routeSubscription = this.routesStore.routes$.subscribe((routes: RouteModel[]) => {
      const sortedRoutes = sortBy(routes, ["sequenceInRouteGroup"]);

      if (this.isDragging) {
        this.pendingRoutes = sortedRoutes;
        return;
      }

      this.routes = sortedRoutes;
      this.cdr.detectChanges();
    })

    this.resourceSubscription = this.resourcesStore.resources$.subscribe((resources: ResourceAllocationTimeWindowExtModel[]) => {
      if (resources?.length) {
        const originalResource = {...this.resource};
        const updatedResource = resources[0];

        this.resource = resources[0];

        if (!!updatedResource) {

          // Clear/Update Routes Data
          if (!updatedResource.scheduleExtraData?.routeIds?.length) {
            this.routesStore.resetRoutes();
          } else if (!_.isEqual(updatedResource.scheduleExtraData?.routeIds, originalResource.scheduleExtraData?.routeIds)) {
            this.routesStore.resetRoutes();
            this.routesStore.getRoutes(this._getRoutesQuery());
          }

        }

        this.cdr.detectChanges();

      }
    })

    if (this.resource) {
      this.resourcesStore.getResources({ids: this.resource.id});
    }

    if (this.resource.scheduleExtraData?.routeIds?.length) {
      this.routesStore.resetRoutes();
      this.routesStore.getRoutes(this._getRoutesQuery());
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.panels || changes.resourcesDropListIds) {
      const filteredPanels = _.filter(this.panels, (p) => {
        return p.id !== this.panel.id;
      })
      this.allPanelIds = _.concat(_.map(filteredPanels, 'id'), this.resourcesDropListIds || []);
    }
    if (changes.visibleIds) {
      this.visibleKeys = {};
      _.forEach(this.visibleIds, (id) => {
        this.visibleKeys[id] = true;
      });
      this.cdr.detectChanges();
    }
  }

  ngOnDestroy() {
    this.routeMetaDataUpdatedSubscription?.unsubscribe();
    this.routeSubscription?.unsubscribe();
    this.routesStore.unSubscribeAll();

    this.resourceSubscription?.unsubscribe();
    this.resourcesStore.unSubscribeAll();
  }

  onDragStarted() {
    this.isDragging = true;
  }

  onDragEnded() {
    this.isDragging = false;

    if (this.pendingRoutes) {
      this.routes = this.pendingRoutes;
      this.pendingRoutes = null;
      this.cdr.detectChanges();
    }
  }

  sortPredicate(index: number, item: CdkDrag<any>, drop: CdkDropList) {
    return true;
  }

  enter(event: CdkDragEnter) {

  }

  drop(event: CdkDragDrop<string[]>) {
    this.isDragging = false;

    this.routesDragAndDropService.onDropResources(
      this.resource,
      event,
      this.selectedServiceIds
    ).then((result) => {
      if (result) {
        if (result.type === 'resequence') {
          this.onChangeResourceSequence.emit(result.data);
        } else if (result.type === 'update') {
          this.onUpdateRouteResource.emit(result.data);
        } else if (result.type === 'add') {
          this.onAddServicesToResource.emit(result.data);
        }
      }

      if (this.pendingRoutes) {
        this.routes = this.pendingRoutes;
        this.pendingRoutes = null;
        this.cdr.detectChanges();
      }
    });
  }

  trackByRouteId(index: number, route: RouteModel): string {
    return route?.id;
  }

  private _getRoutesQuery(): RoutesQueryModel {

    const query = {
      ids: this.resource.scheduleExtraData?.routeIds,
      expandServices: false,
      expandTasks: false,
      expandTasksExt: false,
      limit: 100
    }

    Object.keys(query).forEach(key => query[key] === undefined ? delete query[key] : {});
    return query;
  }


}
